import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { wizardApi } from '@/services/api';
import { Loader2, Star } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Shown once, right after order submission + file upload succeed.
 * Purely a "how was this experience" survey -- separate from the
 * per-job "rate the finished work" flow (jobsApi.rateJob) that fires
 * once a job has actually been completed and filed.
 */
const OrderFeedbackDialog = ({ open, onClose, jobIds = [] }) => {
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStars(0);
    setHoverStars(0);
    setComment('');
  };

  const handleSkip = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (stars === 0) return;
    setSubmitting(true);
    try {
      await wizardApi.submitFeedback(stars, comment, jobIds);
      toast.success('Thanks for the feedback!');
      reset();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleSkip(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>How was your experience?</DialogTitle>
          <DialogDescription>
            Your order has been submitted. Let us know how the ordering and upload process went.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex justify-center gap-1" role="radiogroup" aria-label="Rating out of 5 stars">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={stars === value}
                aria-label={`${value} star${value === 1 ? '' : 's'}`}
                onClick={() => setStars(value)}
                onMouseEnter={() => setHoverStars(value)}
                onMouseLeave={() => setHoverStars(0)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  className={`w-8 h-8 ${
                    (hoverStars || stars) >= value
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground'
                  }`}
                />
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-comment">What could we improve? (optional)</Label>
            <Textarea
              id="feedback-comment"
              placeholder="Tell us what went well or what we could do better..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleSkip} disabled={submitting}>
            Skip
          </Button>
          <Button onClick={handleSubmit} disabled={stars === 0 || submitting}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Submit Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderFeedbackDialog;
